import { CloudIcon, MonitorIcon } from "lucide-react";

import { cn } from "../lib/utils";
import {
  projectImportDestinationLabel,
  type ProjectImportDestinationKind,
} from "../projectImportStore";

interface ProjectImportDestinationPickerProps {
  readonly allowedDestinations: readonly ProjectImportDestinationKind[];
  readonly onSelect: (destination: ProjectImportDestinationKind) => void;
  readonly selectedDestination: ProjectImportDestinationKind;
  /**
   * Leading label for the row. Import views clone an existing repository, so
   * they read "Clone to"; the blank-workspace view creates one from nothing and
   * reads "Create in". The control itself is identical either way.
   */
  readonly label?: string;
}

export function ProjectImportDestinationPicker({
  allowedDestinations,
  onSelect,
  selectedDestination,
  label = "Clone to",
}: ProjectImportDestinationPickerProps) {
  if (allowedDestinations.length <= 1) {
    return null;
  }

  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/60 px-5 py-2.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div
        role="group"
        aria-label="Project import destination"
        className="inline-flex items-center rounded-md bg-muted/50 p-0.5"
      >
        {allowedDestinations.map((destination) => {
          const selected = selectedDestination === destination;
          const Icon = destination === "forge-cloud" ? CloudIcon : MonitorIcon;
          const destinationLabel = projectImportDestinationLabel(destination);
          return (
            <button
              key={destination}
              type="button"
              aria-label={`${label} ${destinationLabel}`}
              aria-pressed={selected}
              className={cn(
                "inline-flex h-7 items-center gap-1.5 rounded-[5px] px-2.5 text-xs font-medium transition-colors",
                selected
                  ? "bg-background text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={() => onSelect(destination)}
            >
              <Icon className="size-3.5" />
              <span>{destinationLabel}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
